/**
 * Conversions between the backend wire types (`api-types.ts`) and the UI domain
 * model (`types.ts`), plus TMDB image URL + poster-palette helpers.
 */

import { IMAGES_BASE_URL, TMDB_IMAGE_BASE, type TmdbImageSize } from './config';
import type {
  ApiCastMember,
  ApiComment,
  ApiCommentAuthor,
  ApiCommunityFeedItem,
  ApiCommunityFeedTitle,
  ApiConnectionUser,
  ApiConnectionState,
  ApiCreateShareResponse,
  ApiEpisode,
  ApiExportJob,
  ApiFriendActivityItem,
  ApiFriendWatchingItem,
  ApiTitleFriend,
  ApiGenre,
  ApiGenresResponse,
  ApiImportJob,
  ApiHomeShow,
  ApiLibraryStats,
  ApiList,
  ApiListItem,
  ApiListPreviewItem,
  ApiNotification,
  ApiOwnProfile,
  ApiSeason,
  ApiSharePreview,
  ApiShareSharer,
  ApiShareTitle,
  ApiShow,
  ApiShowDetail,
  ApiStats,
  ApiUserActivityItem,
  ApiUserCard,
  ApiUserSearchResult,
} from './api-types';
import type {
  CommentAuthor,
  CommunityComment,
  CommunityFeedItem,
  CommunityFeedTitle,
  CommunityTarget,
  CommunityTitleType,
  ConnectionState,
  ConnectionUser,
  DataExportJob,
  DataImportJob,
  Episode,
  Genre,
  LibraryStats,
  NotifCommentPreview,
  NotifItem,
  OwnProfile,
  Person,
  Relationship,
  Season,
  Share,
  SharePreview,
  ShareSharer,
  ShareTitleCard,
  ShowStatus,
  Stats,
  Title,
  UserActivityItem,
  UserCard,
  UserList,
  UserListItem,
  UserSearchResult,
  UserShowStatus,
  TitleFriend,
  WatchingFriend,
} from './types';

const PALETTES: [string, string][] = [
  ['#1E3A8A', '#0F172A'],
  ['#B45309', '#7C2D12'],
  ['#7F1D1D', '#1C1917'],
  ['#134E4A', '#052E2B'],
  ['#6D28D9', '#1E1B4B'],
  ['#0E7490', '#082F49'],
  ['#9D174D', '#4A044E'],
  ['#166534', '#052E16'],
  ['#334155', '#0F172A'],
  ['#0369A1', '#082F49'],
];

const VALID_USER_STATUS: ReadonlySet<string> = new Set(['watchlist', 'watching', 'completed', 'dropped', 'removed']);

/** Deterministic two-color palette so gradient posters look intentional. */
export function paletteFromTitle(title: string): [string, string] {
  let hash = 0;
  for (let i = 0; i < title.length; i++) {
    hash = (hash * 31 + title.charCodeAt(i)) >>> 0;
  }
  return PALETTES[hash % PALETTES.length];
}

export function tmdbImageUrl(path: string | null, size: TmdbImageSize): string | undefined {
  return path ? `${TMDB_IMAGE_BASE}/${size}${path}` : undefined;
}

/** Resolve a mirrored S3 image key against the CloudFront images origin. */
export function s3ImageUrl(key: string | null | undefined): string | undefined {
  return key ? `${IMAGES_BASE_URL}/${key}` : undefined;
}

/** Prefer the S3 mirror (`*_key`), falling back to the TMDB CDN (`*_path`). */
export function imageUrl(key: string | null | undefined, path: string | null | undefined, size: TmdbImageSize): string | undefined {
  return imageUrls(key, path, size).primary;
}

/** Keep both origins so the image component can recover without an app restart. */
export function imageUrls(key: string | null | undefined, path: string | null | undefined, size: TmdbImageSize) {
  const mirrored = s3ImageUrl(key);
  const upstream = tmdbImageUrl(path ?? null, size);
  return { primary: mirrored ?? upstream, fallback: mirrored ? upstream : undefined };
}

export function statusFromApi(status: string | null): ShowStatus {
  if (!status) return 'upcoming';
  const s = status.toLowerCase();
  if (s.includes('ended') || s.includes('cancel')) return 'ended';
  if (s.includes('planned')) return 'upcoming';
  return 'airing';
}

export function userStatusFromApi(status: string | null | undefined): UserShowStatus | null {
  return status && VALID_USER_STATUS.has(status) ? (status as UserShowStatus) : null;
}

/** Canonical positive integer genre id, or null for malformed wire/UI data. */
export function normalizeGenreId(value: unknown): string | null {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const raw = String(value).trim();
  if (!/^\d+$/.test(raw)) return null;
  const numeric = Number(raw);
  return Number.isSafeInteger(numeric) && numeric > 0 ? String(numeric) : null;
}

/** Drop malformed genre rows at the wire boundary before they become chips. */
export function normalizeGenres(res: ApiGenresResponse): ApiGenre[] {
  const genres = Array.isArray(res) ? res : res.genres;
  if (!Array.isArray(genres)) return [];
  return genres.flatMap((genre) => {
    const id = normalizeGenreId(genre?.id);
    const name = typeof genre?.name === 'string' ? genre.name.trim() : '';
    return id && name ? [{ ...genre, id: Number(id), name }] : [];
  });
}

export function mapGenre(g: ApiGenre): Genre {
  return { id: normalizeGenreId(g.id) ?? '', name: g.name, mediaType: g.media_type };
}

/** Map a `cast_data` row into the UI `Person` model. */
export function mapCastMember(c: ApiCastMember): Person {
  const image = imageUrls(c.profile_key, c.profile_path, 'w185');
  return {
    id: String(c.id),
    name: c.name,
    role: c.character ?? '',
    imageUrl: image.primary,
    imageFallbackUrl: image.fallback,
  };
}

/** Map a backend show into the UI `Title` model (no user fields). */
export function mapShowToTitle(show: ApiShow): Title {
  const type = show.item_type === 'movie' ? 'movie' : 'show';
  const releaseDate = type === 'movie' ? show.release_date : show.first_air_date;
  const year = releaseDate ? Number(releaseDate.slice(0, 4)) || 0 : 0;
  const poster = imageUrls(show.poster_key, show.poster_path, 'w500');
  const backdrop = imageUrls(show.backdrop_key, show.backdrop_path, 'w780');
  return {
    id: String(show.tmdb_id),
    type,
    title: show.title ?? '',
    titleBr: show.title_br ?? undefined,
    year,
    status:
      type === 'movie' && show.status?.toLowerCase() === 'released'
        ? 'ended'
        : statusFromApi(show.status ?? null),
    rating: show.vote_average ? Math.round((show.vote_average / 2) * 10) / 10 : 0,
    genres: (show.genres ?? []).filter((g) => normalizeGenreId(g.id) !== null).map(mapGenre),
    networks: [],
    providers: [],
    synopsis: show.overview ?? '',
    synopsisBr: show.overview_br ?? undefined,
    cast: (show.cast_data ?? []).filter((c) => !!c.name).map(mapCastMember),
    directors: show.directors ?? [],
    creators: show.creators ?? [],
    trailer: show.trailer ?? undefined,
    posterUrl: poster.primary,
    posterFallbackUrl: poster.fallback,
    backdropUrl: backdrop.primary,
    backdropFallbackUrl: backdrop.fallback,
    palette: paletteFromTitle(show.title ?? ''),
    seasons: [],
    totalRuntimeMinutes: type === 'movie' ? Math.round(show.runtime ?? 0) : 0,
    ingestStatus: show.ingest_status ?? undefined,
  };
}

/** `/v1/home` watching row → `Title` with the user's status + progress attached. */
export function mapHomeShowToTitle(show: ApiHomeShow): Title {
  return {
    ...mapShowToTitle(show),
    userStatus: userStatusFromApi(show.user_status),
    rewatchCount: Math.round(show.rewatch_count ?? 0),
    userRating: show.rating_user ?? null,
    watchedEpisodes: show.watched_episodes ?? undefined,
    totalEpisodes: show.total_episodes ?? undefined,
    progressPercent: show.progress_percent ?? undefined,
  };
}

/** `/v1/shows/:id` → `Title` with the user's status + rating + progress attached. */
export function mapShowDetailToTitle(show: ApiShowDetail): Title {
  return {
    ...mapShowToTitle(show),
    userStatus: userStatusFromApi(show.user?.status),
    userRating: show.user?.rating ?? null,
    watchedAt: show.user?.watched_at ?? null,
    rewatchCount: Math.round(show.user?.rewatch_count ?? 0),
    watchedEpisodes: show.user?.watched_episodes ?? undefined,
    totalEpisodes: show.user?.total_episodes ?? undefined,
    progressPercent: show.user?.progress_percent ?? undefined,
  };
}

/** `/v1/lists` row → domain `UserList`. */
export function mapList(l: ApiList): UserList {
  return {
    id: l.list_id,
    name: l.name,
    description: l.description ?? undefined,
    showCount: Math.round(l.show_count ?? 0),
    createdAt: l.created_at,
    updatedAt: l.updated_at,
    preview: l.preview?.map(mapListPreview),
  };
}

/** Preview snapshot inside `GET /v1/lists` → domain `UserListItem`. */
export function mapListPreview(p: ApiListPreviewItem): UserListItem {
  const poster = imageUrls(p.poster_key, p.poster_path, 'w500');
  return {
    showId: String(p.show_id),
    type: p.title_type === 'movie' ? 'movie' : 'show',
    title: p.title ?? '',
    titleBr: p.title_br ?? undefined,
    posterUrl: poster.primary,
    posterFallbackUrl: poster.fallback,
    addedAt: '',
  };
}

/** `/v1/lists/:id/shows` membership row → domain `UserListItem`. */
export function mapListItem(i: ApiListItem): UserListItem {
  const poster = imageUrls(i.poster_key, i.poster_path, 'w500');
  return {
    showId: String(i.show_id),
    type: i.title_type === 'movie' ? 'movie' : 'show',
    title: i.title,
    titleBr: i.title_br ?? undefined,
    posterUrl: poster.primary,
    posterFallbackUrl: poster.fallback,
    addedAt: i.added_at,
  };
}

/** Map a backend episode into the UI `Episode` model. */
export function mapEpisode(e: ApiEpisode): Episode {
  const seasonNumber = Math.round(e.season_number);
  const episodeNumber = Math.round(e.episode_number);
  const still = imageUrls(e.still_key, e.still_path, 'w300');
  return {
    id: `${e.show_id}-s${seasonNumber}e${episodeNumber}`,
    seasonNumber,
    episodeNumber,
    title: e.name ?? `Episode ${episodeNumber}`,
    titleBr: e.name_br ?? undefined,
    airDate: e.air_date ?? null,
    runtimeMinutes: Math.round(e.runtime ?? 0),
    watched: !!e.watched,
    userRating: e.user_rating ?? null,
    watchedAt: e.watched_at ?? null,
    stillUrl: still.primary,
    stillFallbackUrl: still.fallback,
    overview: e.overview ?? '',
    overviewBr: e.overview_br ?? undefined,
    voteAverage: e.vote_average ?? undefined,
  };
}

/** Map a backend season summary (episodes loaded lazily) into the UI `Season`. */
export function mapSeasonSummary(s: ApiSeason): Season {
  const poster = imageUrls(s.poster_key, s.poster_path, 'w300');
  return {
    seasonNumber: Math.round(s.season_number),
    episodeCount: Math.round(s.episode_count),
    episodes: [],
    name: s.name ?? undefined,
    nameBr: s.name_br ?? undefined,
    overview: s.overview ?? undefined,
    overviewBr: s.overview_br ?? undefined,
    posterUrl: poster.primary,
    posterFallbackUrl: poster.fallback,
    airDate: s.air_date ?? null,
    watchedEpisodes: s.watched_episodes,
  };
}

/** Inverse mapping, used so the mock client can also serve the raw endpoints. */
export function titleToApiShow(t: Title): ApiShow {
  return {
    tmdb_id: t.id,
    item_type: t.type,
    title: t.title,
    original_title: t.title,
    overview: t.synopsis,
    poster_path: null,
    backdrop_path: null,
    vote_average: t.rating * 2,
    vote_count: null,
    popularity: null,
    first_air_date: t.type === 'show' && t.year ? `${t.year}-01-01` : null,
    last_air_date: null,
    release_date: t.type === 'movie' && t.year ? `${t.year}-01-01` : null,
    runtime: t.type === 'movie' ? t.totalRuntimeMinutes : null,
    status: t.status === 'airing' ? 'Returning Series' : t.status === 'ended' ? 'Ended' : 'Planned',
    number_of_seasons: t.seasons.length,
    number_of_episodes: t.seasons.reduce((a, s) => a + s.episodeCount, 0),
    origin_country: [],
    original_language: null,
    genres: t.genres.map((g) => ({ id: Number(g.id) || 0, name: g.name })),
    trailer: t.trailer ?? null,
  };
}

// --- Social mappers ---

export function mapConnectionState(s: ApiConnectionState): ConnectionState {
  return { following: s.following, requested: s.requested, followedBy: s.followed_by };
}

export function mapLibraryStats(s: ApiLibraryStats): LibraryStats {
  return {
    watching: Math.round(s.watching ?? 0),
    completed: Math.round(s.completed ?? 0),
    watchlist: Math.round(s.watchlist ?? 0),
    dropped: Math.round(s.dropped ?? 0),
    total: Math.round(s.total ?? 0),
  };
}

/** Map the private stats wire contract and contain malformed numeric values. */
export function mapStats(s: ApiStats): Stats {
  const count = (value: number | null | undefined) =>
    Number.isFinite(value) ? Math.max(0, Math.round(value ?? 0)) : 0;
  const share = (value: number | null | undefined) =>
    Number.isFinite(value) ? Math.min(1, Math.max(0, value ?? 0)) : 0;
  return {
    generatedAt: s.generated_at ?? '',
    totals: {
      titlesTracked: count(s.totals?.titles_tracked),
      showsWatched: count(s.totals?.shows_watched),
      moviesWatched: count(s.totals?.movies_watched),
      uniqueMovies: count(s.totals?.unique_movies),
      episodesWatched: count(s.totals?.episodes_watched),
      minutesWatched: count(s.totals?.minutes_watched),
      completedTitles: count(s.totals?.completed_titles),
      rewatches: count(s.totals?.rewatches),
      currentStreakDays: count(s.totals?.current_streak_days),
      longestStreakDays: count(s.totals?.longest_streak_days),
      activeDays: count(s.totals?.active_days),
    },
    genres: (s.genres ?? []).flatMap((genre) => {
      const name = typeof genre?.name === 'string' ? genre.name.trim() : '';
      if (!name) return [];
      return [{
        id: String(genre.id ?? ''),
        name,
        minutes: count(genre.minutes),
        share: share(genre.share),
      }];
    }),
    topTitles: (s.top_titles ?? []).flatMap((entry) =>
      entry?.title?.tmdb_id != null && entry.title.title
        ? [{ title: mapShowToTitle(entry.title), minutes: count(entry.minutes) }]
        : [],
    ),
    activity: {
      startDate: s.activity?.start_date ?? '',
      endDate: s.activity?.end_date ?? '',
      days: (s.activity?.days ?? []).flatMap((day) => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(day?.date ?? '')) return [];
        return [{
          date: day.date,
          minutes: count(day.minutes),
          episodes: count(day.episodes),
          movies: count(day.movies),
        }];
      }),
    },
    dataQuality: {
      runtimeEstimated: s.data_quality?.runtime_estimated === true,
      undatedViewings: count(s.data_quality?.undated_viewings),
    },
  };
}

export function mapOwnProfile(p: ApiOwnProfile): OwnProfile {
  return {
    publicUserId: p.public_user_id,
    username: p.username,
    displayName: p.display_name ?? null,
    avatarUrl: s3ImageUrl(p.avatar_key) ?? null,
    bio: p.bio ?? null,
    socials: p.socials ?? {},
    visibility: p.visibility,
    discoverable: p.discoverable,
    status: p.status,
    followersCount: Math.round(p.followers_count ?? 0),
    followingCount: Math.round(p.following_count ?? 0),
    pendingOutCount: Math.round(p.pending_out_count ?? 0),
    pendingInCount: Math.round(p.pending_in_count ?? 0),
    renameAvailableAt: p.rename_available_at ?? null,
    profileCreatedAt: p.profile_created_at,
    usernameAutoAssigned: p.username_auto_assigned ?? false,
    avatarPalette: paletteFromTitle(p.username ?? ''),
    showcaseIds: (p.showcase_ids ?? []).map(String),
    showcase: (p.showcase ?? []).map(mapHomeShowToTitle),
  };
}

export function mapUserCard(c: ApiUserCard): UserCard {
  const canView = c.can_view_library;
  return {
    publicUserId: c.public_user_id,
    username: c.username,
    displayName: c.display_name ?? null,
    avatarUrl: s3ImageUrl(c.avatar_key) ?? null,
    visibility: c.visibility,
    isPrivate: c.is_private,
    canViewLibrary: canView,
    profileCreatedAt: c.profile_created_at,
    bio: (canView ? c.bio : null) ?? null,
    socials: (canView ? c.socials : null) ?? {},
    followersCount: canView ? Math.round(c.followers_count ?? 0) : 0,
    followingCount: canView ? Math.round(c.following_count ?? 0) : 0,
    avatarPalette: paletteFromTitle(c.username ?? ''),
    showcase: canView ? (c.showcase ?? []).map(mapHomeShowToTitle) : [],
    connection: mapConnectionState(c.connection),
  };
}

export function mapConnectionUser(u: ApiConnectionUser): ConnectionUser {
  return {
    publicUserId: u.public_user_id,
    username: u.username ?? null,
    displayName: u.display_name ?? null,
    avatarUrl: s3ImageUrl(u.avatar_key) ?? null,
    since: u.since,
    avatarPalette: paletteFromTitle(u.username ?? u.public_user_id),
  };
}

/** Keep only a valid 1-5 integer rating; anything else is "no rating". */
export function validActivityRating(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const rounded = Math.round(value);
  return rounded >= 1 && rounded <= 5 ? rounded : undefined;
}

export function mapFriendActivityItem(item: ApiFriendActivityItem): UserActivityItem {
  const friend = item.friend;
  const mapped: UserActivityItem = {
    ...mapUserActivityItem(item),
    friend: {
      publicUserId: friend.public_user_id,
      username: friend.username,
      displayName: friend.display_name ?? null,
      avatarUrl: s3ImageUrl(friend.avatar_key) ?? null,
      avatarPalette: paletteFromTitle(friend.username),
    },
  };
  // Additive `rating` (docs/api-schema.md GET /v1/friends/activity): only a
  // valid 1-5 value survives; `rewatch` rows never carry one.
  const rating = item.kind === 'rewatch' ? undefined : validActivityRating(item.rating);
  if (rating !== undefined) mapped.rating = rating;
  return mapped;
}

/** `GET /v1/friends/watching` row → domain; null when the row is unusable. */
export function mapFriendWatchingItem(row: ApiFriendWatchingItem): WatchingFriend | null {
  if (!row.friend || !row.title || row.title.tmdb_id == null || row.title.tmdb_id === '') return null;
  const name = row.title.title ?? '';
  // w185: these render as small avatar rings; the mirrored key wins.
  const poster = imageUrls(row.title.poster_key, row.title.poster_path, 'w185');
  // w500 for the full-screen story; the mirrored key has one size.
  const large = imageUrls(row.title.poster_key, row.title.poster_path, 'w500');
  const mapped: WatchingFriend = {
    friend: mapCommentAuthor(row.friend),
    title: {
      id: String(row.title.tmdb_id),
      title: name,
      titleBr: row.title.title_br ?? undefined,
      type: row.title.title_type === 'movie' ? 'movie' : 'show',
      posterUrl: poster.primary,
      posterFallbackUrl: poster.fallback,
      posterLargeUrl: large.primary,
      posterLargeFallbackUrl: large.fallback,
      palette: paletteFromTitle(name || String(row.title.tmdb_id)),
    },
    seen: row.seen === true,
    updatedAt: row.updated_at ?? '',
  };
  const rating = validActivityRating(row.title.rating_user);
  if (rating !== undefined) mapped.rating = rating;
  const ep = row.last_watched;
  if (ep && Number.isFinite(ep.season_number) && Number.isFinite(ep.episode_number)) {
    mapped.lastWatched = {
      seasonNumber: Math.round(ep.season_number),
      episodeNumber: Math.round(ep.episode_number),
      watchedAt: ep.watched_at ?? undefined,
      rating: validActivityRating(ep.rating),
      name: ep.name || undefined,
      nameBr: ep.name_br || undefined,
    };
  }
  if (row.comment?.comment_id && row.comment.body) {
    mapped.comment = {
      id: row.comment.comment_id,
      body: row.comment.body,
      createdAt: row.comment.created_at ?? undefined,
    };
  }
  return mapped;
}

const TITLE_FRIEND_STATUSES = new Set<TitleFriend['status']>(['watching', 'completed', 'dropped', 'watchlist']);

/** `GET /v1/shows/:id/friends` row → domain; null for unknown statuses or missing friends. */
export function mapTitleFriend(row: ApiTitleFriend): TitleFriend | null {
  const status = row.status as TitleFriend['status'];
  if (!row.friend?.username || !TITLE_FRIEND_STATUSES.has(status)) return null;
  const mapped: TitleFriend = { friend: mapCommentAuthor(row.friend), status };
  const rating = validActivityRating(row.rating);
  if (rating !== undefined) mapped.rating = rating;
  const ep = row.last_watched;
  if (ep && Number.isFinite(ep.season_number) && Number.isFinite(ep.episode_number)) {
    mapped.lastWatched = { seasonNumber: Math.round(ep.season_number), episodeNumber: Math.round(ep.episode_number) };
  }
  return mapped;
}

export function mapUserActivityItem(item: ApiUserActivityItem): UserActivityItem {
  return {
    id: item.id,
    kind: item.kind,
    watchedAt: item.watched_at,
    title: mapShowToTitle(item.title),
    seasonNumber: item.season_number == null ? undefined : Math.round(item.season_number),
    episodeNumber: item.episode_number == null ? undefined : Math.round(item.episode_number),
  };
}

export function mapUserSearchResult(u: ApiUserSearchResult): UserSearchResult {
  return {
    publicUserId: u.public_user_id,
    username: u.username,
    displayName: u.display_name ?? null,
    avatarUrl: s3ImageUrl(u.avatar_key) ?? null,
    isPrivate: u.is_private,
    avatarPalette: paletteFromTitle(u.username),
    iFollow: u.i_follow ?? false,
    followsMe: u.follows_me ?? false,
    iRequested: u.i_requested ?? false,
    followersCount: typeof u.followers_count === 'number' ? u.followers_count : undefined,
  };
}

export function relationshipFrom(iFollow: boolean, followsMe: boolean): Relationship {
  if (iFollow && followsMe) return 'mutual';
  if (iFollow) return 'following';
  if (followsMe) return 'follows_you';
  return 'none';
}

export function mapNotifItem(n: ApiNotification): NotifItem {
  const commentTarget = n.comment_target
    ? {
        titleType: n.comment_target.title_type as CommunityTitleType,
        tmdbId: n.comment_target.tmdb_id,
        seasonNumber: n.comment_target.season_number,
        episodeNumber: n.comment_target.episode_number,
      }
    : undefined;
  return {
    type: n.notif_type,
    actorPublicId: n.actor_public_id,
    actorUsername: n.actor_username,
    actorDisplayName: n.actor_display_name ?? null,
    avatarUrl: s3ImageUrl(n.actor_avatar_key) ?? null,
    createdAt: n.notif_created_at,
    read: n.read ?? false,
    avatarPalette: paletteFromTitle(n.actor_username ?? ''),
    commentId: n.comment_id,
    upvoteCount: n.upvote_count,
    parentCommentId: n.parent_comment_id ?? null,
    ancestors: n.comment_ancestors,
    commentTarget,
    ...mapNotifHydration(n),
  };
}

/**
 * The additive `comment_preview` / `title` hydration on comment
 * notifications. Both stay optional: an older backend (or an unresolvable
 * comment/catalog row) omits them and every surface degrades without a
 * per-row fetch. A malformed optional field is dropped, never thrown on.
 */
function mapNotifHydration(n: ApiNotification): Pick<NotifItem, 'preview' | 'titleCard'> {
  const out: Pick<NotifItem, 'preview' | 'titleCard'> = {};
  const preview = n.comment_preview as unknown;
  if (preview && typeof preview === 'object') {
    const p = preview as { body?: unknown; has_image?: unknown; state?: unknown };
    const state: NotifCommentPreview['state'] =
      p.state === 'visible' || p.state === 'deleted' ? p.state : 'hidden';
    out.preview = {
      body: state === 'visible' && typeof p.body === 'string' ? p.body.trim() : '',
      hasImage: p.has_image === true,
      state,
    };
  }
  const card = n.title;
  if (card && typeof card === 'object' && card.tmdb_id) {
    const poster = imageUrls(card.poster_key, card.poster_path, 'w185');
    const name = (card.title ?? '').trim();
    out.titleCard = {
      tmdbId: String(card.tmdb_id),
      titleType: card.title_type === 'movie' ? 'movie' : 'show',
      title: name,
      titleBr: card.title_br?.trim() || undefined,
      posterUrl: poster.primary ?? undefined,
      posterFallbackUrl: poster.fallback ?? undefined,
      palette: paletteFromTitle(name || String(card.tmdb_id)),
      seasonNumber: typeof card.season_number === 'number' ? card.season_number : undefined,
      episodeNumber: typeof card.episode_number === 'number' ? card.episode_number : undefined,
    };
  }
  return out;
}

// --- Community mappers ---
//
// Wire → domain for the community contract. These only ever see the public
// comment payload — the backend strips `owner_sub` and the raw DynamoDB keys
// before a comment is serialized, and we never reintroduce them here.

const VALID_MODERATION_STATUS: ReadonlySet<string> = new Set([
  'visible',
  'pending',
  'hidden',
  'deleted',
]);

/** Non-negative integer guard for DynamoDB numbers that arrive as floats. */
function count(value: number | null | undefined): number {
  return Number.isFinite(value) ? Math.max(0, Math.round(value ?? 0)) : 0;
}

export function mapCommentAuthor(a: ApiCommentAuthor): CommentAuthor {
  return {
    publicUserId: a.public_user_id,
    username: a.username,
    displayName: a.display_name ?? null,
    avatarUrl: s3ImageUrl(a.avatar_key) ?? null,
    avatarPalette: paletteFromTitle(a.username ?? a.public_user_id),
  };
}

export function mapComment(c: ApiComment): CommunityComment {
  const status = VALID_MODERATION_STATUS.has(c.moderation_status)
    ? c.moderation_status
    : 'visible';
  return {
    commentId: c.comment_id,
    locale: c.locale,
    target: c.target,
    originTarget: c.origin_target,
    parentCommentId: c.parent_comment_id ?? null,
    rootCommentId: c.root_comment_id ?? null,
    depth: count(c.depth),
    score: count(c.score),
    replyCount: count(c.reply_count),
    createdAt: c.created_at,
    editedAt: c.edited_at ?? null,
    moderationStatus: status,
    viewerVoted: !!c.viewer_voted,
    body: c.body ?? null,
    author: c.author ? mapCommentAuthor(c.author) : null,
    ...(c.mentions ? {
      mentions: c.mentions.map((mention) => ({
        publicUserId: mention.public_user_id,
        username: mention.username,
        usernameAtPost: mention.username_at_post,
      })),
    } : {}),
    ...(c.title ? { historyTitle: mapCommunityFeedTitle(c.title) } : {}),
    mediaIds: c.media_ids,
    lastReplyPreview: c.last_reply_preview
      ? {
          commentId: c.last_reply_preview.comment_id,
          body: c.last_reply_preview.body,
          createdAt: c.last_reply_preview.created_at,
          author: c.last_reply_preview.author
            ? mapCommentAuthor(c.last_reply_preview.author)
            : null,
        }
      : undefined,
  };
}

/** Map a community list envelope's rows, dropping any malformed entries. */
export function mapCommentList(comments: ApiComment[] | undefined): CommunityComment[] {
  return (comments ?? [])
    .filter((c): c is ApiComment => Boolean(c && c.comment_id))
    .map(mapComment);
}

/** Map a community feed row (following feed carries the friend card). */
export function mapCommunityFeedItem(item: ApiCommunityFeedItem): CommunityFeedItem {
  const mapped: CommunityFeedItem = {
    comment: mapComment(item.comment),
    title: mapCommunityFeedTitle(item.title),
  };
  if (item.friend) {
    mapped.friend = mapCommentAuthor(item.friend);
  }
  return mapped;
}

/** Lean feed title card: only what rendering + deep-linking need. */
export function mapCommunityFeedTitle(t: ApiCommunityFeedTitle): CommunityFeedTitle {
  const isMovie = t.title_type === 'movie';
  const releaseDate = isMovie ? t.release_date : t.first_air_date;
  const year = releaseDate ? Number(releaseDate.slice(0, 4)) || 0 : 0;
  const poster = imageUrls(t.poster_key, t.poster_path, 'w500');
  return {
    titleType: isMovie ? 'movie' : 'show',
    id: String(t.tmdb_id),
    title: t.title ?? '',
    titleBr: t.title_br ?? undefined,
    posterUrl: poster.primary ?? null,
    posterFallbackUrl: poster.fallback ?? null,
    palette: paletteFromTitle(t.title ?? ''),
    year: year || undefined,
  };
}

/** Synthesize a minimal `Title` from a lean feed card (friends feed rows). */
export function titleFromFeedCard(t: CommunityFeedTitle): Title {
  return {
    id: t.id,
    type: t.titleType,
    title: t.title,
    titleBr: t.titleBr,
    year: t.year ?? 0,
    status: 'airing',
    rating: 0,
    genres: [],
    networks: [],
    providers: [],
    synopsis: '',
    cast: [],
    directors: [],
    creators: [],
    posterUrl: t.posterUrl ?? undefined,
    posterFallbackUrl: t.posterFallbackUrl ?? undefined,
    palette: t.palette,
    seasons: [],
    totalRuntimeMinutes: 0,
  };
}

/**
 * Stable identity string for a discussion target (movie/show/season/episode).
 * A bare TMDB id is not a safe identity — the title type is always included.
 */
export function communityTargetKey(target: CommunityTarget): string {
  const base = `${target.titleType}:${target.tmdbId}`;
  if (target.titleType === 'season') return `${base}:${target.seasonNumber ?? 0}`;
  if (target.titleType === 'episode') {
    return `${base}:${target.seasonNumber ?? 0}:${target.episodeNumber ?? 0}`;
  }
  return base;
}

/** Normalized target identity string (the backend's target grammar). */
export function communityTargetString(target: CommunityTarget): string {
  if (target.titleType === 'movie') return `MOVIE#${target.tmdbId}`;
  if (target.titleType === 'show') return `SHOW#${target.tmdbId}`;
  if (target.titleType === 'season') {
    return `SHOW#${target.tmdbId}#SEASON#${target.seasonNumber ?? 0}`;
  }
  return `SHOW#${target.tmdbId}#SEASON#${target.seasonNumber ?? 0}#EP#${target.episodeNumber ?? 0}`;
}

/** Query params for the community list/create routes from a structured target. */
const _int = (n: number | undefined): number => Math.round(n ?? 0);

/** `ApiExportJob` → domain `DataExportJob`. */
export function mapDataExportJob(j: ApiExportJob): DataExportJob {
  const c = j.counts ?? {};
  return {
    id: j.export_id,
    status: j.status,
    format: j.format === 'csv' ? 'csv' : 'json',
    counts: {
      shows: _int(c.shows),
      movies: _int(c.movies),
      episodes: _int(c.episodes),
      lists: _int(c.lists),
    },
    byteSize: j.byte_size === undefined ? undefined : _int(j.byte_size),
    downloadUrl: j.download_url || undefined,
    expiresAt: j.expires_at,
    createdAt: j.created_at || undefined,
    error: j.error || undefined,
  };
}

/** `ApiImportJob` → domain `DataImportJob`. */
export function mapDataImportJob(j: ApiImportJob): DataImportJob {
  const counts: Record<string, number> = {};
  for (const [k, v] of Object.entries(j.counts ?? {})) counts[k] = _int(v);
  return {
    id: j.import_id,
    status: j.status,
    sourceFormat: j.source_format || undefined,
    sourceHint: j.source_hint || undefined,
    detected: j.detected
      ? {
          format: j.detected.format,
          files: j.detected.files ?? [],
          hintMismatch: Boolean(j.detected.hint_mismatch),
        }
      : undefined,
    counts,
    duplicateOf: j.duplicate_of || undefined,
    reportUrl: j.report_url || undefined,
    error: j.error || undefined,
  };
}

export function communityTargetQuery(
  target: CommunityTarget,
): Record<string, string | number> {
  const query: Record<string, string | number> = {
    title_type: target.titleType,
    tmdb_id: target.tmdbId,
  };
  if (target.titleType === 'season' || target.titleType === 'episode') {
    if (target.seasonNumber !== undefined) query.season_number = target.seasonNumber;
  }
  if (target.titleType === 'episode' && target.episodeNumber !== undefined) {
    query.episode_number = target.episodeNumber;
  }
  return query;
}

// --- Sharing (docs/sharing-plan.md §11) ------------------------------------

export function mapShare(s: ApiCreateShareResponse): Share {
  return { code: s.code, url: s.url, kind: s.kind, createdAt: s.created_at };
}

export function mapShareSharer(s: ApiShareSharer): ShareSharer {
  return {
    username: s.username,
    displayName: s.display_name ?? null,
    avatarUrl: s3ImageUrl(s.avatar_key) ?? null,
  };
}

/** A share preview's title card — image handling matches `mapShowToTitle`
 *  (prefer the mirrored `*_key`, fall back to the TMDB `*_path`), but this is
 *  a smaller shape: only what the public resolver actually returns. */
export function mapShareTitle(t: ApiShareTitle): ShareTitleCard {
  const poster = imageUrls(t.poster_key, t.poster_path, 'w500');
  const backdrop = imageUrls(t.backdrop_key, t.backdrop_path, 'w780');
  return {
    tmdbId: String(t.tmdb_id),
    type: t.title_type,
    title: t.title ?? '',
    titleBr: t.title_br ?? undefined,
    year: t.year ?? undefined,
    synopsis: t.overview ?? undefined,
    synopsisBr: t.overview_br ?? undefined,
    posterUrl: poster.primary,
    posterFallbackUrl: poster.fallback,
    backdropUrl: backdrop.primary,
    backdropFallbackUrl: backdrop.fallback,
    palette: paletteFromTitle(t.title ?? ''),
    genres: t.genres ?? [],
  };
}

export function mapSharePreview(p: ApiSharePreview): SharePreview {
  return {
    code: p.code,
    kind: p.kind,
    createdAt: p.created_at,
    titleType: p.target.title_type,
    tmdbId: String(p.target.tmdb_id),
    note: p.note ?? null,
    sharer: p.sharer ? mapShareSharer(p.sharer) : null,
    title: mapShareTitle(p.title),
  };
}
